import { Loading } from '@/components';
import { useAccountInfoContext } from '@/hooks/useAccountInfo';
import { Button } from '@bfe/moly';
import { useFm } from '@web3/base-hooks';
import { useButtonStatus } from './hooks';
import { Overview } from './overview';
import { OperationTab } from '@/types/common';
import { ClaimType } from '@/types/claim';
import ProcessingIcon from '@/assets/images/bybitsol/processing.svg';
import { formatNumber } from '@web3/base-utils';
import { useMemo } from 'react';
import { AirdropActivity } from '@/types/bbsol';
import cls from 'classnames';

import styles from './index.module.less';

interface Props {
  setShowModal: (v: boolean) => void;
  onOpenClaimModal: (v: ClaimType) => void;
  onChangeTab: (v: OperationTab) => void;
  activity: AirdropActivity;
}

export const InvestInfo = (props: Props) => {
  const { setShowModal, onOpenClaimModal, onChangeTab, activity } = props;

  const fm = useFm();
  const { accountInfo, isFetching } = useAccountInfoContext();
  const {
    unstakeIsHidden,
    unstakeIsDisabled,
    unstakeEmergencyDisabled,
    claimDisabled,
  } = useButtonStatus();

  const { processingUnstakeAmount, claimableTokenAmount } = accountInfo || {};

  const hasInvested =
    accountInfo?.isInitStatus !== undefined && !accountInfo?.isInitStatus;

  const enableRedeem = !unstakeIsHidden && !unstakeEmergencyDisabled;

  const showDelayClaim = useMemo(() => {
    return Boolean(claimableTokenAmount) && !claimDisabled;
  }, [claimableTokenAmount, claimDisabled]);

  const showProcessing = useMemo(() => {
    return Boolean(processingUnstakeAmount) && !showDelayClaim;
  }, [showDelayClaim, processingUnstakeAmount]);

  if (isFetching) return <Loading />;

  return (
    <div className={styles.container}>
      <div className={styles.delayProcessContainer}>
        {/* 延迟赎回中的数目 */}
        {showProcessing && (
          <div
            className={cls(
              styles.processingContainer,
              activity ? styles.activity : ''
            )}
          >
            <ProcessingIcon />
            <div className={styles.processingUnstakeAmount}>
              {fm('bbSolRedemptionNum', {
                NUM: `${formatNumber(processingUnstakeAmount, {
                  kmb: true,
                  decimal: 4,
                  smallNumberUseValidDigits: false,
                  collapseZero: false,
                })}`,
              })}
            </div>
          </div>
        )}
        {/* 延迟赎回可赎回的数目 */}
        {showDelayClaim && (
          <div
            className={cls(
              styles.claimContainer,
              activity ? styles.activity : ''
            )}
            onClick={() => {
              onOpenClaimModal(ClaimType.RedeemPrincipal);
            }}
          >
            <ProcessingIcon />
            <div className={styles.processingUnstakeAmount}>
              {fm('solRedemptionAvailNum', {
                NUM: `${formatNumber(claimableTokenAmount, {
                  kmb: true,
                  decimal: 4,
                  smallNumberUseValidDigits: false,
                  collapseZero: false,
                })}`,
              })}
            </div>
            <div className={styles.dot} />
          </div>
        )}
      </div>
      {/* 持有和收益信息 */}
      <Overview />
      {/* 赎回按钮 */}
      {hasInvested && (
        <Button
          className={styles.redeem}
          variant="primary"
          size="large"
          disabled={unstakeIsDisabled || !enableRedeem}
          onClick={() => setShowModal(true)}
        >
          {fm('redeem')}
        </Button>
      )}
      {!hasInvested && (
        <Button
          className={styles.stake}
          variant="primary"
          size="large"
          onClick={() => onChangeTab(OperationTab.Stake)}
        >
          {fm('staking_go_to_invest')}
        </Button>
      )}
    </div>
  );
};
